import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

/**
 * ProphetLightbox
 * ---------------
 * Minimal full-screen viewer for the centered NFT artwork. Uses the
 * original image file supplied in `PROPHETS[*].image` (no re-render of
 * the carousel card, no name / family / metadata overlays).
 *
 * Behaviour (per spec):
 * - Opens ONLY when the already-centered card is clicked.
 * - Overlay covers the viewport with a near-black background + subtle blur.
 * - Artwork is centered, preserves its native square aspect ratio, and is
 *   scaled to fit the viewport with comfortable margins.
 * - A very thin silver-tone frame sits OUTSIDE the image (never crops).
 * - Close via X button (top-right), overlay click, or Escape.
 * - Clicking the image itself does NOT close.
 * - Respects `prefers-reduced-motion`.
 * - Radix Dialog handles focus trap, background scroll lock (data attr),
 *   ESC handling, and returns focus to the trigger on close.
 */
export function ProphetLightbox({ prophet, open, onOpenChange, restoreFocusRef }) {
  const stopClose = (e) => e.stopPropagation();

  // Return focus to the centered carousel card after close.
  const handleCloseAutoFocus = React.useCallback((e) => {
    if (restoreFocusRef?.current) {
      e.preventDefault();
      restoreFocusRef.current.focus({ preventScroll: true });
    }
  }, [restoreFocusRef]);

  if (!prophet) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="prophets-lightbox-overlay"
          data-testid="prophets-lightbox-overlay"
        />
        <DialogPrimitive.Content
          aria-label={`${prophet.name || "Prophet"} — full-screen artwork`}
          data-testid="prophets-lightbox"
          className="prophets-lightbox-content"
          onCloseAutoFocus={handleCloseAutoFocus}
          onOpenAutoFocus={(e) => {
            // Radix would auto-focus the first tabbable element (the close
            // button). That's fine — nothing else to focus in this modal.
          }}
        >
          {/* Hidden title for a11y — never rendered visually. */}
          <DialogPrimitive.Title className="sr-only">
            {prophet.name ? `${prophet.name} — full artwork` : "Prophet artwork"}
          </DialogPrimitive.Title>

          {/* The framed image. Clicking it must NOT close the lightbox. */}
          <div
            className="prophets-lightbox-frame"
            onClick={stopClose}
            data-testid="prophets-lightbox-frame"
          >
            <img
              src={prophet.image}
              alt={prophet.alt || prophet.name || "Prophet artwork"}
              draggable={false}
              className="prophets-lightbox-image"
              data-testid="prophets-lightbox-image"
            />
          </div>

          <DialogPrimitive.Close
            className="prophets-lightbox-close"
            aria-label="Close artwork viewer"
            data-testid="prophets-lightbox-close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
